import React, { useMemo, useState } from "react";
import { ArrowUpRight, X, Minus, Plus } from "lucide-react";
import { buildPartnerUrl, openExternal } from "../../../data/partnerLinks";

const Stepper = ({ label, testid, value, min, onChange }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-brand-ink">{label}</span>
    <div className="flex items-center gap-3">
      <button
        type="button"
        data-testid={`${testid}-dec`}
        onClick={() => onChange(Math.max(min, value - 1))}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line text-brand-ink disabled:opacity-40"
        disabled={value <= min}
      >
        <Minus size={14} />
      </button>
      <span data-testid={testid} className="w-5 text-center text-sm font-medium text-brand-ink">{value}</span>
      <button
        type="button"
        data-testid={`${testid}-inc`}
        onClick={() => onChange(value + 1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line text-brand-ink"
      >
        <Plus size={14} />
      </button>
    </div>
  </div>
);

export const DateDialog = ({ parent, pending, onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  const inAWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const isTourRadar = pending?.partner === "TourRadar";

  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const dt = new Date(now.getFullYear(), now.getMonth() + i, 1);
      opts.push({
        value: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`,
        label: dt.toLocaleString("en", { month: "long", year: "numeric" }),
      });
    }
    return opts;
  }, []);

  const [month, setMonth] = useState(monthOptions[0].value);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(inAWeek);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  if (!pending) return null;

  const confirm = () => {
    const startDate = isTourRadar ? `${month}-01` : from;
    const finalUrl = buildPartnerUrl({
      partner: pending.partner,
      title: pending.placeTitle,
      country: pending.country,
      query: pending.query,
      bookType: pending.bookType,
      from: startDate,
      to: isTourRadar ? undefined : to,
      adults,
      children,
    });
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        destination_id: parent.id,
        sub_id: pending.id,
        partner: pending.partner,
        url: finalUrl,
        date_from: startDate,
        date_to: isTourRadar ? null : to,
        adults,
        children,
      }),
    }).catch(() => {});
    onClose();
    openExternal(finalUrl);
  };

  return (
    <div data-testid="date-dialog" className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-4 md:items-center">
      <div className="w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline text-brand-inkSoft">When & who is travelling?</p>
            <h3 className="font-display mt-2 text-2xl leading-tight">{pending.title}</h3>
            <p className="mt-1 text-sm text-brand-inkSoft">{pending.location}</p>
          </div>
          <button data-testid="date-dialog-close" onClick={onClose} className="rounded-full p-2 text-brand-inkSoft hover:bg-brand-bg2">
            <X size={16} />
          </button>
        </div>

        {isTourRadar ? (
          <label className="mt-6 block">
            <span className="overline text-brand-inkSoft">Travel month</span>
            <select
              data-testid="month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink"
            >
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="overline text-brand-inkSoft">Check-in</span>
              <input
                data-testid="date-from"
                type="date"
                value={from}
                min={today}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink"
              />
            </label>
            <label className="block">
              <span className="overline text-brand-inkSoft">Check-out</span>
              <input
                data-testid="date-to"
                type="date"
                value={to}
                min={from}
                onChange={(e) => setTo(e.target.value)}
                className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2.5 text-sm text-brand-ink"
              />
            </label>
          </div>
        )}

        <div className="mt-5 space-y-3 rounded-xl bg-brand-bg2 p-4">
          <Stepper label="Adults" testid="adults-count" value={adults} min={1} onChange={setAdults} />
          <Stepper label="Children" testid="children-count" value={children} min={0} onChange={setChildren} />
        </div>

        <button
          data-testid="date-dialog-confirm"
          onClick={confirm}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-ink px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-brand-inkSoft"
        >
          Continue to {pending.partner} <ArrowUpRight size={14} />
        </button>
        <p className="mt-3 text-center text-xs text-brand-inkSoft">
          {isTourRadar
            ? `Your travel month and travellers are passed to ${pending.partner}.`
            : `Your dates and travellers are passed to ${pending.partner} so you see matching packages.`}
        </p>
      </div>
    </div>
  );
};

export default DateDialog;
